import { useCallback, useState } from 'react';
import localforage from 'localforage';

interface UseExpandedStateOptions {
  viewId: number;
  storageKey?: string;
}

export const useExpandedState = ({ viewId, storageKey = 'expandedGroups' }: UseExpandedStateOptions) => {
  // Separate state for groups and subtasks
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedSubtasks, setExpandedSubtasks] = useState<Set<number>>(new Set());
  const [isInitialized, setIsInitialized] = useState(false);

  // Load expanded state from localStorage
  const loadExpandedState = useCallback(async () => {
    try {
      const key = `${storageKey}-${viewId}`;
      const stored = await localforage.getItem<{
        groups: string[];
        subtasks: number[];
      }>(key);

      if (stored) {
        setExpandedGroups(new Set(stored.groups || []));
        setExpandedSubtasks(new Set(stored.subtasks || []));
      } else {
        // By default, all groups are expanded
        setExpandedGroups(new Set());
      }
      setIsInitialized(true);
    } catch (error) {
      console.error('Failed to load expanded state:', error);
      setIsInitialized(true);
    }
  }, [viewId, storageKey]);

  // Save expanded state to localStorage
  const saveExpandedState = useCallback(
    async (groups: Set<string>, subtasks: Set<number>) => {
      try {
        const key = `${storageKey}-${viewId}`;
        await localforage.setItem(key, {
          groups: Array.from(groups),
          subtasks: Array.from(subtasks),
        });
      } catch (error) {
        console.error('Failed to save expanded state:', error);
      }
    },
    [viewId, storageKey]
  );

  // Toggle group expanded state
  const toggleGroup = useCallback(
    (groupId: string) => {
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        if (next.has(groupId)) {
          next.delete(groupId);
        } else {
          next.add(groupId);
        }
        void saveExpandedState(next, expandedSubtasks);
        return next;
      });
    },
    [expandedSubtasks, saveExpandedState]
  );

  // Toggle subtask expanded state
  const toggleSubtask = useCallback(
    (taskId: number) => {
      setExpandedSubtasks((prev) => {
        const next = new Set(prev);
        if (next.has(taskId)) {
          next.delete(taskId);
        } else {
          next.add(taskId);
        }
        void saveExpandedState(expandedGroups, next);
        return next;
      });
    },
    [expandedGroups, saveExpandedState]
  );

  // Expand all groups (clear collapsed set = all expanded)
  const expandAllGroups = useCallback(
    (_groupIds: string[]) => {
      const next = new Set<string>();
      setExpandedGroups(next);
      void saveExpandedState(next, expandedSubtasks);
    },
    [expandedSubtasks, saveExpandedState]
  );

  // Collapse all groups (add all to collapsed set)
  const collapseAllGroups = useCallback((groupIds: string[]) => {
    const next = new Set(groupIds);
    setExpandedGroups(next);
    void saveExpandedState(next, expandedSubtasks);
  }, [expandedSubtasks, saveExpandedState]);

  return {
    expandedGroups,
    expandedSubtasks,
    toggleGroup,
    toggleSubtask,
    expandAllGroups,
    collapseAllGroups,
    loadExpandedState,
    isInitialized,
  };
};
